import { Flag, Bot, Trash2, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmButton, timeAgo } from "./AdminShared";

export default function ReportsTab({ reports, onResolve, onModerate }) {
  if (reports.length === 0) {
    return (
      <div className="card-surface p-10 text-center text-sm text-zinc-500">
        No open reports. The vault is clean.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {reports.map((r) => (
        <div key={r.id} data-testid={`admin-report-${r.id}`} className="card-surface p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-none bg-rose-600/15 flex items-center justify-center shrink-0">
              <Flag size={14} className="text-rose-500" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                {r.target_type} · reported by {r.reporter?.display_name} · {timeAgo(r.created_at)} ago
              </div>
              <div className="text-sm text-zinc-100 leading-relaxed mb-2 break-words">"{r.reason}"</div>
              {r.target && r.target_type === "post" && (
                <div className="border-l-2 border-white/10 pl-3 text-sm text-zinc-400 line-clamp-3">
                  {r.target.text || "(post deleted)"}
                </div>
              )}
              {r.target && r.target_type === "user" && (
                <div className="border-l-2 border-white/10 pl-3 text-sm text-zinc-400">
                  {r.target.display_name} — {r.target.display_name}
                </div>
              )}
              {r.target && r.target_type === "live" && (
                <div className="border-l-2 border-white/10 pl-3 text-sm text-zinc-400">
                  Stream: {r.target.title} {r.target.is_live ? "(LIVE)" : "(ended)"}
                </div>
              )}
              {!r.target && (
                <div className="text-xs text-zinc-600 italic">Target already removed.</div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 flex-wrap">
            {r.target && (
              <Button
                size="sm"
                onClick={() => onModerate({
                  type: r.target_type === "live" ? "stream" : r.target_type,
                  id: r.target_id,
                })}
                className="rounded-none text-xs bg-transparent border border-white/15 text-white hover:border-rose-600"
                data-testid={`admin-report-${r.id}-ai`}
              >
                <Bot size={13} /> AI Review
              </Button>
            )}
            <ConfirmButton
              testid={`admin-report-${r.id}-dismiss`}
              label="Dismiss this report?"
              description="Marks the report as resolved without any action taken."
              onConfirm={() => onResolve(r.id, "dismissed")}
            >
              Dismiss
            </ConfirmButton>
            {r.target && (
              <ConfirmButton
                testid={`admin-report-${r.id}-remove`}
                variant="danger"
                label="Remove the reported content?"
                description="Deletes the reported post / ends the reported stream and marks the report resolved."
                onConfirm={() => onResolve(r.id, "content_removed")}
              >
                <Trash2 size={13} /> Remove content
              </ConfirmButton>
            )}
            <ConfirmButton
              testid={`admin-report-${r.id}-ban`}
              variant="danger"
              label="Ban the offending user?"
              description="Suspends the reported user (or the post/stream author). They will not be able to log in."
              onConfirm={() => onResolve(r.id, "user_banned")}
            >
              <Ban size={13} /> Ban user
            </ConfirmButton>
          </div>
        </div>
      ))}
    </div>
  );
}
